import { isAxiosError } from 'axios'
import type { AxiosError } from 'axios'

import { runtime } from '@/config/env'

import { captureException } from './sentry'

const isDev = runtime.isDev

function isNetworkError(error: AxiosError | Error | any): boolean {
  if (!isAxiosError(error)) return false

  const status = error.response?.status
  const message = error.message?.toLowerCase()
  const code = error.code?.toLowerCase()

  return status === 0 || (!status && (message === 'network error' || code === 'err_network'))
}

type LoggerContext = {
  contexts?: Record<string, any>
  [key: string]: any
}

function parseBrowserFromUserAgent(userAgent: string | undefined): { name?: string; version?: string } {
  if (!userAgent) return {}

  const patterns: Array<{ name: string; regex: RegExp }> = [
    { name: 'Edge', regex: /Edg\/(\d+\.[\d.]+)/ },
    { name: 'Chrome', regex: /Chrome\/(\d+\.[\d.]+)/ },
    { name: 'Firefox', regex: /Firefox\/(\d+\.[\d.]+)/ },
    { name: 'Safari', regex: /Version\/(\d+\.[\d.]+).*Safari\// },
  ]

  for (const pattern of patterns) {
    const match = userAgent.match(pattern.regex)
    if (match?.[1]) return { name: pattern.name, version: match[1] }
  }

  return {}
}

function parseOsFromUserAgent(userAgent: string | undefined): { name?: string; version?: string } {
  if (!userAgent) return {}

  const windowsMatch = userAgent.match(/Windows NT (\d+\.\d+)/)
  if (windowsMatch?.[1]) {
    const nt = windowsMatch[1]
    const mapped: Record<string, string> = {
      '10.0': '10',
      '6.3': '8.1',
      '6.2': '8',
      '6.1': '7',
    }
    return { name: 'Windows', version: mapped[nt] ?? nt }
  }

  const androidMatch = userAgent.match(/Android (\d+(?:\.\d+)*)/)
  if (androidMatch?.[1]) return { name: 'Android', version: androidMatch[1] }

  const iosMatch = userAgent.match(/(iPhone|iPad|iPod).*OS (\d+[_\d]*)/)
  if (iosMatch?.[2]) return { name: 'iOS', version: iosMatch[2].replace(/_/g, '.') }

  const macMatch = userAgent.match(/Mac OS X (\d+[_\d]*)/)
  if (macMatch?.[1]) return { name: 'macOS', version: macMatch[1].replace(/_/g, '.') }

  if (userAgent.includes('Linux')) return { name: 'Linux' }

  return {}
}

function getClientContext(): Record<string, any> | undefined {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return undefined

  const connection = (navigator as any).connection
  const globalPrivacyControl = (navigator as any).globalPrivacyControl
  const userAgentData = (navigator as any).userAgentData
  const displayMode = window.matchMedia?.('(display-mode: standalone)').matches ? 'standalone' : 'browser'

  const browserInfo = parseBrowserFromUserAgent(navigator.userAgent)
  const osInfo = parseOsFromUserAgent(navigator.userAgent)

  const deviceType = window.matchMedia?.('(pointer: coarse)').matches ? 'mobile' : 'desktop'

  return {
    browser: {
      ...browserInfo,
      language: navigator.language,
      languages: navigator.languages,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      globalPrivacyControl,
      webdriver: navigator.webdriver,
      displayMode,
    },
    os: {
      ...osInfo,
      platform: userAgentData?.platform,
    },
    device: {
      type: deviceType,
      screen_width: window.screen?.width,
      screen_height: window.screen?.height,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
      online: navigator.onLine,
    },
    ...(connection && {
      network: {
        effective_type: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
        saveData: connection.saveData,
      },
    }),
  }
}

function getNavigationContext(): Record<string, any> | undefined {
  if (typeof window === 'undefined') return undefined

  return {
    navigation: {
      current_url: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      referrer: document.referrer,
      user_agent: navigator.userAgent,
    },
  }
}

function withClientContext(context?: LoggerContext): LoggerContext | undefined {
  const clientContext = getClientContext()
  const navigationContext = getNavigationContext()

  if (!clientContext && !navigationContext) return context

  return {
    ...context,
    contexts: {
      ...clientContext,
      ...navigationContext,
      ...(context?.contexts || {}),
    },
  }
}

/**
 * Parse URL search params into object
 */
function parseQueryParams(url: string): Record<string, any> {
  try {
    const urlObj = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost')
    const params: Record<string, any> = {}
    urlObj.searchParams.forEach((value, key) => {
      params[key] = value
    })
    return params
  } catch {
    return {}
  }
}

/**
 * Sanitize headers by only including safe debugging headers
 */
function sanitizeHeaders(headers: any): any {
  if (!headers) return {}

  const safeHeaders = [
    'accept',
    'content-type',
    'user-agent',
    'x-locale',
    'x-branch-id',
    'x-requested-with',
    'cache-control',
    'pragma',
  ]

  const sanitized: any = {}
  Object.keys(headers).forEach((key) => {
    if (safeHeaders.includes(key.toLowerCase())) {
      sanitized[key] = headers[key]
    }
  })

  return sanitized
}

/**
 * Sanitize response data to avoid logging sensitive information
 * Production-safe: Logs only metadata, errors, and diagnostic info - NOT user data
 */
function sanitizeResponseData(data: any, maxDepth: number = 3): any {
  if (maxDepth <= 0) return '[Max Depth Reached]'

  if (!data) return data

  // Primitive types
  if (typeof data !== 'object') {
    return String(data).length > 1000 ? String(data).substring(0, 1000) + '...[truncated]' : data
  }

  // Arrays
  if (Array.isArray(data)) {
    // Laravel stack traces - summarize only
    if (data.length > 0 && data[0]?.file && data[0]?.line && data[0]?.function) {
      return `[Stack Trace: ${data.length} frames]`
    }

    // Large arrays - limit items
    if (data.length > 10) {
      return [
        ...data.slice(0, 5).map((item) => sanitizeResponseData(item, maxDepth - 1)),
        `...[${data.length - 5} more items]`,
      ]
    }
    return data.map((item) => sanitizeResponseData(item, maxDepth - 1))
  }

  // Objects
  const sensitiveKeys = [
    'password',
    'passwd',
    'pwd',
    'token',
    'access_token',
    'refresh_token',
    'bearer',
    'secret',
    'webhook_secret',
    'client_secret',
    'authorization',
    'auth',
    'api_key',
    'apiKey',
    'apikey',
    'key',
    'private_key',
    'public_key',
    'ssn',
    'social_security',
    'credit_card',
    'card_number',
    'cvv',
    'cvc',
  ]

  const piiKeys = [
    'email',
    'phone',
    'mobile',
    'telephone',
    'address',
    'street',
    'city',
    'zip',
    'postal',
    'passport',
    'license',
    'national_id',
    'ip',
    'ip_address',
  ]

  // Keys to keep for debugging (metadata and error info only)
  const allowedKeys = [
    'meta',
    'metadata',
    '_meta',
    'status',
    'code',
    'statusCode',
    'status_code',
    'message',
    'error',
    'error_message',
    'error_code',
    'exception',
    'file',
    'line',
    'timestamp',
    'request_id',
    'trace_id',
    'api_version',
    'version',
    'schema_version',
    'isAvailableData',
    'client',
    'env',
  ]

  const sanitized: any = {}
  let keyCount = 0
  const maxKeys = 30 // Reduced for production

  for (const key in data) {
    const lowerKey = key.toLowerCase()

    // PRODUCTION RULE: Skip 'data' field entirely (contains user/business data)
    if (key === 'data' && typeof data[key] === 'object') {
      const dataValue = data[key]
      // If data is an object, show structure summary only
      if (dataValue && typeof dataValue === 'object') {
        const dataKeys = Object.keys(dataValue)
        sanitized[key] =
          `[Data Object: ${dataKeys.length} keys - ${dataKeys.slice(0, 5).join(', ')}${dataKeys.length > 5 ? '...' : ''}]`
      } else {
        sanitized[key] = '[Data Object]'
      }
      keyCount++
      continue
    }

    if (keyCount >= maxKeys) {
      sanitized['...'] = `[${Object.keys(data).length - maxKeys} more keys omitted]`
      break
    }

    // Redact all sensitive credentials
    if (sensitiveKeys.some((sensitive) => lowerKey.includes(sensitive))) {
      sanitized[key] = '[REDACTED]'
      keyCount++
      continue
    }

    // Partially mask PII
    if (piiKeys.some((pii) => lowerKey.includes(pii))) {
      const value = data[key]
      if (typeof value === 'string' && value.length > 0) {
        sanitized[key] = value.length > 6 ? `${value.substring(0, 3)}***${value.substring(value.length - 2)}` : '***'
      } else {
        sanitized[key] = value
      }
      keyCount++
      continue
    }

    // Laravel exception trace - summarize
    if (key === 'trace' && Array.isArray(data[key])) {
      sanitized[key] = `[Stack Trace: ${data[key].length} frames]`
      keyCount++
      continue
    }

    // Keep allowed metadata/error fields
    if (allowedKeys.includes(lowerKey)) {
      sanitized[key] = sanitizeResponseData(data[key], maxDepth - 1)
      keyCount++
      continue
    }

    // For other keys, only log structure in production (not values)
    if (!isDev && typeof data[key] === 'object' && data[key] !== null) {
      if (Array.isArray(data[key])) {
        sanitized[key] = `[Array: ${data[key].length} items]`
      } else {
        sanitized[key] = `[Object: ${Object.keys(data[key]).length} keys]`
      }
    } else {
      sanitized[key] = sanitizeResponseData(data[key], maxDepth - 1)
    }

    keyCount++
  }

  return sanitized
}

/**
 * Sanitizes error objects for production logging
 */
function sanitizeError(error: any): any {
  // Skip logging expected errors
  if (isAxiosError(error)) {
    if (error.response?.status === 401 || error.response?.status === 422) {
      return null
    }
  }

  if (isDev) {
    return error // Full error in development
  }

  // Production: log minimal info
  if (isAxiosError(error)) {
    return {
      type: 'AxiosError',
      status: error.response?.status,
      message: error.message,
      code: error.code,
    }
  }

  if (error instanceof Error) {
    return {
      type: error.name,
      message: error.message,
      stack: error.stack?.split('\n').slice(0, 5).join('\n'), // First 5 lines of stack
    }
  }

  return { type: 'Unknown', message: String(error) }
}

/**
 * Extract detailed request information from AxiosError
 */
function getDetailedRequestContext(error: AxiosError): Record<string, any> {
  const method = error.config?.method?.toUpperCase() || 'UNKNOWN'
  const fullUrl = error.config?.url || 'unknown'
  const baseUrl = error.config?.baseURL || ''
  const endpoint = fullUrl.replace(baseUrl, '')
  const queryParams = parseQueryParams(fullUrl)

  return {
    request: {
      method,
      url: fullUrl,
      endpoint,
      base_url: baseUrl,
      query_params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
      headers: sanitizeHeaders(error.config?.headers),
      data: error.config?.data instanceof FormData ? '[FormData]' : sanitizeResponseData(error.config?.data),
      timeout: error.config?.timeout,
      timestamp: new Date().toISOString(),
    },
  }
}

/**
 * Extract detailed response information from AxiosError
 * Production-safe: Logs metadata and errors only, not user data
 */
function getDetailedResponseContext(error: AxiosError): Record<string, any> {
  const status = error.response?.status
  const statusText = error.response?.statusText
  const responseData = error.response?.data

  return {
    response: {
      status,
      status_text: statusText,
      headers: sanitizeHeaders(error.response?.headers),
      data: sanitizeResponseData(responseData),
      error_message: (responseData as any)?.message || error.message,
      validation_errors: (responseData as any)?.errors,
      error_code: (responseData as any)?.code,
      exception: (responseData as any)?.exception,
      timestamp: new Date().toISOString(),
    },
  }
}

/**
 * Get error stack trace with context
 */
function getErrorStackContext(error: Error): Record<string, any> {
  return {
    error_stack: {
      name: error.name,
      message: error.message,
      stack: error.stack,
      stack_lines: error.stack?.split('\n').slice(0, 10) || [],
    },
  }
}

/**
 * Centralized logger with detailed error tracking
 *
 * @example
 * ```ts
 * logger.info('User logged in successfully')
 * logger.info('Checkout initiated', { userId: 123 }, { sendToSentry: true })
 * logger.warn('Rate limit approaching')
 * logger.error('Failed to fetch user', error)
 * logger.debug('State updated', newState)
 * logger.apiError('Failed to create order', error)
 * ```
 */
export const logger = {
  /**
   * Log informational messages
   */
  info: (message: string, context?: any, options?: { sendToSentry?: boolean }) => {
    if (isDev) {
      if (context) {
        console.info('[INFO]:', message, context)
      } else {
        console.info('[INFO]:', message)
      }
    }

    if (options?.sendToSentry) {
      const infoError = new Error(message)
      infoError.name = 'Info'
      captureException(
        infoError,
        withClientContext({
          level: 'info',
          contexts: context ? { details: context } : undefined,
        })
      )
    }
  },

  /**
   * Log warnings with automatic error sanitization
   */
  warn: (message: string, error?: AxiosError | Error | any) => {
    if (!error) {
      if (isDev) {
        console.warn('[WARN]:', message)
      }
      return
    }

    const sanitized = sanitizeError(error)
    if (!sanitized) return

    if (isAxiosError(error)) {
      const method = error.config?.method?.toUpperCase() || 'UNKNOWN'
      const endpoint = error.config?.url?.replace(error.config?.baseURL || '', '') || 'unknown'
      const status = error.response?.status

      if (isDev) {
        console.warn(`[WARN] ${message}: ${method} ${endpoint} (${status})`)
        console.warn('Request:', error.config)
        console.warn('Response:', error.response?.data)
      }

      const sentryError = new Error(`${message}: ${method} ${endpoint} (${status})`)
      sentryError.name = 'APIWarning'

      captureException(
        sentryError,
        withClientContext({
          level: 'warning',
          tags: {
            api_warning: true,
            http_method: method,
            http_status: status?.toString() || 'unknown',
            endpoint: endpoint,
          },
          contexts: {
            api: {
              message,
              method,
              url: endpoint,
              status,
            },
            ...getDetailedRequestContext(error),
            ...getDetailedResponseContext(error),
          },
        })
      )
    } else if (error instanceof Error) {
      if (isDev) {
        console.warn('[WARN]:', message, error)
      }

      captureException(
        error,
        withClientContext({
          level: 'warning',
          contexts: {
            ...getErrorStackContext(error),
          },
        })
      )
    } else {
      if (isDev) {
        console.warn('[WARN]:', message, sanitized)
      }
    }
  },

  /**
   * Log errors with automatic sanitization and Sentry reporting
   */
  error: (message: string, error?: AxiosError | Error | any) => {
    if (!error) {
      if (isDev) {
        console.error('[ERROR]:', message)
      }
      return
    }

    const sanitized = sanitizeError(error)
    if (!sanitized) return

    if (isAxiosError(error)) {
      if (!isDev && isNetworkError(error)) {
        const method = error.config?.method?.toUpperCase() || 'UNKNOWN'
        const endpoint = error.config?.url?.replace(error.config?.baseURL || '', '') || 'unknown'

        const sentryError = new Error(`${message}: ${method} ${endpoint} (Network Error)`)
        sentryError.name = 'NetworkError'

        captureException(
          sentryError,
          withClientContext({
            level: 'warning',
            tags: {
              network_error: true,
              http_method: method,
              http_status: '0',
              endpoint: endpoint,
            },
            contexts: {
              api: {
                message,
                method,
                url: endpoint,
                status: 0,
              },
              ...getDetailedRequestContext(error),
              ...getDetailedResponseContext(error),
            },
          })
        )

        return
      }

      const method = error.config?.method?.toUpperCase() || 'UNKNOWN'
      const endpoint = error.config?.url?.replace(error.config?.baseURL || '', '') || 'unknown'
      const status = error.response?.status

      if (isDev) {
        console.error(`[ERROR] ${message}: ${method} ${endpoint} ${error.response} (${status})`)
      }

      const sentryError = new Error(`${message}: ${method} ${endpoint} (${status})`)
      sentryError.name = 'APIError'

      captureException(
        sentryError,
        withClientContext({
          level: 'error',
          tags: {
            api_error: true,
            http_method: method,
            http_status: status?.toString() || 'unknown',
            endpoint: endpoint,
          },
          contexts: {
            api: {
              message,
              method,
              url: endpoint,
              status,
            },
            ...getDetailedRequestContext(error),
            ...getDetailedResponseContext(error),
          },
        })
      )
    } else if (error instanceof Error) {
      if (isDev) {
        console.error('[ERROR]:', message, error)
      }

      captureException(
        error,
        withClientContext({
          level: 'error',
          contexts: {
            ...getErrorStackContext(error),
          },
        })
      )
    } else {
      if (isDev) {
        console.error('[ERROR]:', message, sanitized)
      }
    }
  },

  /**
   * Log debug messages (development only)
   */
  debug: (message: string, context?: any) => {
    if (isDev) {
      if (context) {
        console.debug('[DEBUG]:', message, context)
      } else {
        console.debug('[DEBUG]:', message)
      }
    }
  },

  /**
   * Log API errors with detailed context for debugging
   *
   * @example
   * ```ts
   * catch (error) {
   *   logger.apiError('Failed to fetch users', error)
   *   logger.apiError('Maintenance mode', error, { skipSentry: true })
   * }
   * ```
   */
  apiError: (message: string, error: AxiosError | Error, options?: { skipSentry?: boolean }) => {
    if (!isAxiosError(error)) {
      logger.error(message, error)
      return
    }

    const status = error.response?.status

    // Skip logging for network errors and undefined status (network failures)
    if (!isDev && (isNetworkError(error) || status === undefined)) {
      logger.warn(message, error)
      return
    }

    // Skip logging for expected errors
    if (status === 401 || status === 422) return

    const method = error.config?.method?.toUpperCase() || 'UNKNOWN'
    const url = error.config?.url || 'unknown'
    const endpoint = url.replace(error.config?.baseURL || '', '')
    const responseData = error.response?.data as any
    const errorMessage = responseData?.message || error.message

    if (isDev) {
      console.error(`[API ERROR] ${method} ${endpoint} (${status}):`, errorMessage, error)
    }

    if (!options?.skipSentry) {
      const sentryError = new Error(`API Error: ${method} ${endpoint} (${status})`)
      sentryError.name = 'APIError'

      captureException(
        sentryError,
        withClientContext({
          level: 'error',
          tags: {
            api_error: true,
            http_method: method,
            http_status: status?.toString() || 'unknown',
            endpoint: endpoint,
          },
          contexts: {
            api: {
              message,
              method,
              url: endpoint,
              full_url: url,
              status,
              error_message: errorMessage,
            },
            ...getDetailedRequestContext(error),
            ...getDetailedResponseContext(error),
          },
        })
      )
    }
  },
}
